import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { nudgeApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, Target, Search } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Nudge, Status } from '@/types/learningManagement';

export const NudgeListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'nudge');
  const canEdit = hasPermission('edit', 'nudge');
  const canDelete = hasPermission('delete', 'nudge');

  // Build fetch function with conditional API calls
  const fetchNudgesFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return nudgeApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else if (filterCategory !== 'all') {
        return nudgeApi.listByCategory(filterCategory, { page: params.page, size: params.size });
      } else {
        return nudgeApi.list({ page: params.page, size: params.size });
      }
    };
  }, [searchQuery, filterCategory]);

  // Use paginated list hook
  const {
    data: allNudges,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Nudge>({
    fetchFn: fetchNudgesFn,
    entityName: 'nudges',
    suppress404Errors: true,
  });

  // Apply client-side status filter
  const nudges = useMemo(() => {
    if (!allNudges) return [];
    if (filterStatus === 'all') return allNudges;
    return allNudges.filter((n) => n.status === filterStatus);
  }, [allNudges, filterStatus]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (nudgeId: string) => {
      await nudgeApi.delete(nudgeId);
    },
    entityName: 'nudge',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (nudgeId: string) => {
    if (!window.confirm('Are you sure you want to delete this nudge?')) return;
    await deleteMutation.mutate(nudgeId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: Status) => {
    switch (status) {
      case 'ACTIVE':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
      case 'READ':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ARCHIVED':
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const nudgeId = (nudge: Nudge) => nudge.nudgeId || nudge.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nudge Management</h1>
          <p className="text-muted-foreground mt-2">Manage all nudges</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/nudges/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Nudge
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search nudges..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="REMINDER">Reminder</SelectItem>
                <SelectItem value="MOTIVATION">Motivation</SelectItem>
                <SelectItem value="HABIT_BUILDING">Habit Building</SelectItem>
                <SelectItem value="PROGRESS_CHECK">Progress Check</SelectItem>
                <SelectItem value="GOAL_ALIGNMENT">Goal Alignment</SelectItem>
                <SelectItem value="SKILL_DEVELOPMENT">Skill Development</SelectItem>
                <SelectItem value="LEARNING_PATH">Learning Path</SelectItem>
                <SelectItem value="ASSESSMENT_PREP">Assessment Prep</SelectItem>
                <SelectItem value="REFLECTION">Reflection</SelectItem>
                <SelectItem value="CELEBRATION">Celebration</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="READ">Read</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="PAUSED">Paused</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Nudges Table */}
      <Card>
        <CardHeader>
          <CardTitle>Nudges ({nudges.length})</CardTitle>
          <CardDescription>List of all nudges</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading nudges..." />
          ) : (error || (allNudges && allNudges.length === 0 && filterStatus === 'all')) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load nudges. Please try again or create a new one." : "Get started by creating your first nudge."}
              icon={Target}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/nudges/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nudges.length === 0 && filterStatus !== 'all' ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No nudges match the selected status filter. Try adjusting your filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    nudges.map((nudge) => (
                    <TableRow key={nudgeId(nudge)}>
                      <TableCell className="font-medium">{nudge.title}</TableCell>
                      <TableCell>{nudge.category}</TableCell>
                      <TableCell>{nudge.nudgeType}</TableCell>
                      <TableCell>{nudge.priority}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(nudge.status)}`}>
                          {nudge.status}
                        </span>
                      </TableCell>
                      <TableCell>{nudge.nudgeGenerated ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {formatDate(nudge.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/nudges/${nudgeId(nudge)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/nudges/${nudgeId(nudge)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(nudgeId(nudge))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NudgeListPage;
